import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail, CheckCircle2, KeyRound } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState(null);

  useEffect(() => { api.get("/church-info").then((r) => setInfo(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Erreur, veuillez réessayer");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-deepblue-900" data-testid="forgot-password-page">
      <div className="absolute inset-0 bg-gradient-to-br from-deepblue-900 via-deepblue-900/95 to-deepblue-800" />
      <div className="absolute inset-0 grain opacity-60" />

      <div className="relative w-full max-w-md mx-4">
        <Link to="/admin/login" className="inline-flex items-center gap-2 text-white/60 hover:text-gold mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> Retour à la connexion
        </Link>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          {done ? (
            <div className="text-center py-4" data-testid="forgot-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center border border-emerald-400/40 mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h1 className="font-serif text-2xl text-white">Vérifiez vos emails</h1>
              <p className="mt-3 text-white/70 text-sm">
                Si un compte existe avec cet email, vous recevrez sous peu un lien pour réinitialiser votre mot de passe.
              </p>
              <p className="mt-2 text-xs text-white/50">Le lien est valable pendant 1 heure.</p>
              <Link to="/admin/login">
                <Button className="mt-6 w-full bg-gold hover:bg-gold-hover text-white rounded-full">
                  Retour à la connexion
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                {info?.logo_url ? (
                  <img src={info.logo_url} alt="Logo" className="w-20 h-20 mx-auto rounded-full object-cover ring-4 ring-gold/40 bg-white mb-4" />
                ) : (
                  <div className="w-16 h-16 mx-auto rounded-full bg-gold/20 flex items-center justify-center border border-gold/40 mb-4">
                    <KeyRound className="w-7 h-7 text-gold" />
                  </div>
                )}
                <h1 className="font-serif text-3xl text-white">Mot de passe oublié ?</h1>
                <p className="mt-2 text-white/60 text-sm">
                  Entrez votre email — nous vous enverrons un lien pour créer un nouveau mot de passe.
                </p>
              </div>

              <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
                <div>
                  <Label htmlFor="email" className="text-white/80 text-xs uppercase tracking-widest">Email de votre compte</Label>
                  <Input id="email" type="email" required autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 bg-white/95 border-transparent text-deepblue-900"
                    placeholder="prenom@canaanmeigda.com"
                    data-testid="forgot-email" />
                </div>

                {error && (
                  <div className="rounded-lg bg-red-500/20 border border-red-500/40 text-red-100 text-sm px-4 py-3">
                    {error}
                  </div>
                )}

                <Button type="submit" disabled={submitting}
                  className="w-full bg-gold hover:bg-gold-hover text-white rounded-full py-6"
                  data-testid="forgot-submit">
                  <Mail className="w-4 h-4 mr-2" />
                  {submitting ? "Envoi…" : "Envoyer le lien de réinitialisation"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
